import React, { FC, useEffect, useRef, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { connect, useDispatch, useSelector } from 'react-redux'
import { Button } from '@material-ui/core'
import Headline from 'uiKit/texts/Headline'
import LoaderScreen from 'uiKit/loaders/loaderScreen'
import Search from 'uiKit/Search/Search'
import { filterBarSearchStyle } from 'uiKit/Search/styles'
import Pagination from 'uiKit/table/Pagination'
import PageHeader from 'uiKit/PageHeader'
import FilterBar from 'uiKit/FilterBar'
import EmptyState from 'uiKit/EmptyState'
import { useDebounce } from 'hooks/useDebounce'
import { DEFAULT_ROWS_PER_PAGE } from 'constants/pagination'
import { ProfessionalsTable } from './components/ProfessionalsTable/ProfessionalsTable'
import { DEFAULT_DEBOUNCE_TIME } from '../../constants/time'
import { updateUserSupervisionStatus } from './actions/usersAction'
import { isQcsBot } from 'constants/qcs'

import {
  ButtonTitle,
  UsersButtonWrap,
  UsersContainer,
  UsersControlLine,
  UserSearchWrap,
  UsersFilterGroup,
  UsersHeadlineWrap,
} from './Professionals.styles'
import {
  InvitationStatusFilter,
  InvitationStatusFilterValue,
} from './components/InvitationStatusFilter/InvitationStatusFilter'
import { deleteUser, getManagers, getUsers, resendUserInvitation } from './api/usersApi'
import { UserType } from './model/usersModel'
import NoChartData from '../../uiKit/NoChartData'
import { InviteUserModal } from './components/InviteUserModal/InviteUserModal'
import { DeleteUserModal } from './components/DeleteUserModal/DeleteUserModal'
import { setIsLoadingUsers, updateCurrentPage, updateLinkStatusFilter } from './actions/usersAction'
import { SupervisionManagementModal } from './components/SupervisionManagementModal/SupervisionManagementModal'
import { ResendInviteModal } from './components/ResendInviteModal/ResendInviteModal'
import DuplicateAttentionModal from '../serviceUsers/components/DuplicateAttentionModal/DuplicateAttentionModal'

export const inputSearchStyles = {
  borderRadius: 10,
  height: '45px',
  padding: '14px 24px',
  minWidth: 200
}

/** CareBrain (master) primary CTA. */
export const buttonStylesCareBrain = {
  background: 'var(--color-button-primary)',
  boxShadow: '0px 3px 9px rgba(19, 69, 186, 0.206267)',
  borderRadius: 10,
  padding: 12,
  height: 45,
  minWidth: 'unset'
}

/** QCS primary CTA — no glow, sentence-case friendly sizing. */
export const buttonStylesQcs = {
  background: 'var(--color-button-primary)',
  boxShadow: 'none',
  borderRadius: 8,
  padding: '0 20px',
  height: 48,
  minWidth: 'unset',
  fontWeight: 600,
  textTransform: 'none' as const,
}

/** @deprecated Prefer buttonStylesCareBrain / buttonStylesQcs. Alias for CareBrain. */
export const buttonStyles = buttonStylesCareBrain

const containerStyle = {
  paddingBottom: 150
}

interface Props {
  users: UserType[]
  currentPage: number
  totalElements: number
  linkStatusFilter: InvitationStatusFilterValue
  match: any
  isLoadingUsers: boolean
}

const Professionals: FC<Props> = ({
  users,
  currentPage,
  totalElements,
  linkStatusFilter,
  match,
  isLoadingUsers
}) => {
  const { params: { botId } } = match
  const activeBot = useSelector((state: any) => state.activeBot)
  const isQcs = isQcsBot(activeBot || botId)
  // QCS hides the invitation-status dropdown — always fetch the full list.
  const effectiveLinkStatus: InvitationStatusFilterValue = isQcs ? 'ALL' : linkStatusFilter
  const [archivedStatus, setArchivedStatus] = useState('')
  const [searchingValue, setSearchingValue] = useState('')
  const [isOpenUserModal, setIsOpenUserModal] = useState(false)
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false)
  const [isOpenSupervisionManagementModal, setIsOpenSupervisionManagementModal] = useState(false)
  const [isOpenResendInviteModal, setIsOpenResendInviteModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserType>(null)
  const [resendInviteUser, setResendInviteUser] = useState<UserType>(null)
  const [duplicateAttentionModal, setDuplicateAttentionModal] = useState({
    isOpen: false,
    duplicateUsers: ''
  })
  const dispatch = useDispatch()

  const updateUserSupervisionStatusHandler = (userId, newStatus) => {
    dispatch(updateUserSupervisionStatus(userId, newStatus))
  }

  const refreshUsers = (page = currentPage) => {
    return getUsers(botId, page, searchingValue, effectiveLinkStatus, isQcs ? archivedStatus : '')
  }

  const refreshUsersFromStart = () => {
    updateCurrentPage(0)
    return getUsers(botId, 0, searchingValue, effectiveLinkStatus, isQcs ? archivedStatus : '')
  }

  // Reset the view when switching organisation. Fetching is not done here —
  // changing these values re-runs the single fetch effect below.
  useEffect(() => {
    setSearchingValue('')
    setArchivedStatus('')
    updateLinkStatusFilter('ALL')
    updateCurrentPage(0)
    getManagers(botId)
  }, [botId])

  useEffect(() => {
    return () => {
      updateCurrentPage(0)
    }
  }, [])

  // Single owner of the professionals fetch.
  const debouncedSearch = useDebounce(searchingValue, DEFAULT_DEBOUNCE_TIME)
  const seqRef = useRef(0)

  useEffect(() => {
    const seq = ++seqRef.current
    setIsLoadingUsers(true)

    getUsers(botId, currentPage, debouncedSearch, effectiveLinkStatus, isQcs ? archivedStatus : '').finally(() => {
      if (seq !== seqRef.current) return
      setIsLoadingUsers(false)
    })
  }, [botId, currentPage, debouncedSearch, effectiveLinkStatus, archivedStatus, isQcs])

  const onChangeHandler = (e) => {
    setSearchingValue(e.target.value)
    updateCurrentPage(0)
  }

  const onChangePageHandler = (e, page) => {
    updateCurrentPage(page)
  }

  const onInvitationStatusFilterChange = (value: InvitationStatusFilterValue) => {
    updateLinkStatusFilter(value)
    updateCurrentPage(0)
  }

  const onClearFilters = () => {
    setSearchingValue('')
    setArchivedStatus('')
    updateLinkStatusFilter('ALL')
    updateCurrentPage(0)
  }

  const hasActiveFilters = isQcs
    ? Boolean(searchingValue.trim()) || Boolean(archivedStatus)
    : Boolean(searchingValue.trim()) || linkStatusFilter !== 'ALL'

  const deleteUserHandler = () => {
    setIsOpenDeleteModal(false)
    deleteUser(selectedUser.id).then(() => {
      refreshUsers()
    })
  }

  const openDeleteModalHandler = (user) => {
    setIsOpenDeleteModal(true)
    setSelectedUser(user)
  }

  const openSupervisionManagementModalHandler = (user) => {
    setIsOpenSupervisionManagementModal(true)
    setSelectedUser(user)
  }

  const openResendInviteModalHandler = (user) => {
    setResendInviteUser(user)
    setIsOpenResendInviteModal(true)
  }

  const resendInviteHandler = () => {
    setIsOpenResendInviteModal(false)
    resendUserInvitation(botId, resendInviteUser?.id).then(() => {
      refreshUsers()
    })
    setResendInviteUser(null)
  }

  const listBody = isLoadingUsers ? (
    <LoaderScreen />
  ) : users.length ? (
    <>
      <ProfessionalsTable
        users={users}
        botId={botId}
        refreshUsers={refreshUsers}
        openDeleteModalHandler={openDeleteModalHandler}
        openSupervisionManagementModalHandler={openSupervisionManagementModalHandler}
        openResendInviteModalHandler={openResendInviteModalHandler}
      />
      <Pagination
        page={currentPage}
        rowsPerPage={DEFAULT_ROWS_PER_PAGE}
        count={totalElements}
        onChangePage={onChangePageHandler}
      />
    </>
  ) : isQcs ? (
    hasActiveFilters ? (
      <EmptyState
        title="No professionals match your filters"
        description="Try a different search term, or clear the filters to see everyone."
      />
    ) : (
      <EmptyState
        title="No professionals yet"
        description="Professionals for this organisation will appear here."
      />
    )
  ) : (
    <NoChartData
      isUserData={true}
      text="You have no Professionals yet. Start by pressing the button -  Invite new Professional"
    />
  )

  return (
    <UsersContainer style={containerStyle}>
      {isQcs ? (
        <>
          <PageHeader title="Professionals" />
          <FilterBar
            search={
              <Search
                value={searchingValue}
                onChange={onChangeHandler}
                placeholder="Search by name or email"
                inputStyle={filterBarSearchStyle}
              />
            }
            onClear={hasActiveFilters ? onClearFilters : undefined}
          />
        </>
      ) : (
        <>
          <UsersHeadlineWrap>
            <Headline title="Professionals" />
          </UsersHeadlineWrap>
          <UsersControlLine>
            <UsersFilterGroup>
              <UserSearchWrap>
                <Search
                  value={searchingValue}
                  onChange={onChangeHandler}
                  placeholder="Search by name or email"
                  inputStyle={inputSearchStyles}
                />
              </UserSearchWrap>
              <InvitationStatusFilter
                value={linkStatusFilter}
                onChange={onInvitationStatusFilterChange}
              />
            </UsersFilterGroup>
            <UsersButtonWrap>
              <Button
                style={buttonStylesCareBrain}
                onClick={() => setIsOpenUserModal(true)}
              >
                <ButtonTitle>Invite new Professional</ButtonTitle>
              </Button>
            </UsersButtonWrap>
          </UsersControlLine>
        </>
      )}

      {listBody}

      {!isQcs && isOpenUserModal &&
        <InviteUserModal
          botId={botId}
          isOpenUserModal={isOpenUserModal}
          onClose={() => setIsOpenUserModal(false)}
          setDuplicateAttentionModal={setDuplicateAttentionModal}
          refreshUsersFromStart={refreshUsersFromStart}
        />
      }
      {isOpenDeleteModal &&
        <DeleteUserModal
          open
          onClose={() => setIsOpenDeleteModal(false)}
          onDelete={deleteUserHandler}
          selectedUser={selectedUser}
        />
      }
      {isOpenSupervisionManagementModal &&
        <SupervisionManagementModal
          isOpenSupervisionManagementModal={isOpenSupervisionManagementModal}
          botId={botId}
          onClose={() => {
            refreshUsers()
            setIsOpenSupervisionManagementModal(false)
          }}
          selectedUser={selectedUser}
          updateUserSupervisionStatus={updateUserSupervisionStatusHandler}
        />
      }
      {!isQcs && isOpenResendInviteModal &&
        <ResendInviteModal
          open
          onClose={() => {
            setIsOpenResendInviteModal(false)
            setResendInviteUser(null)
          }}
          onResend={resendInviteHandler}
        />
      }

      {duplicateAttentionModal.isOpen &&
        <DuplicateAttentionModal
          open={duplicateAttentionModal.isOpen}
          userNames={duplicateAttentionModal.duplicateUsers}
          onClose={() => setDuplicateAttentionModal(prevState => ({ ...prevState, isOpen: false}))}
          category='PROFESSIONAL'
        />
      }
    </UsersContainer>
  )
}

const mapStateToProps = (state) => ({
  users: state.professionalsTab.users,
  currentPage: state.professionalsTab.currentPage,
  totalPages: state.professionalsTab.totalPages,
  totalElements: state.professionalsTab.totalElements,
  linkStatusFilter: state.professionalsTab.linkStatusFilter,
  isLoadingUsers: state.professionalsTab.isLoadingUsers
})

export default withRouter(connect(mapStateToProps)(Professionals))
